import { logger } from '@/lib/logger';

import { breakpoints } from '@/styles/util';

import { getSessionReplayId, getSessionReplayUrl } from '@/utils/amplitude';

declare global {
  interface Window {
    Pylon: ((...args: unknown[]) => void) & { q: unknown[]; e: (e: unknown) => void };
    __matchaPylonThemeSyncCleanup?: () => void;
    pylon: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
      };
    };
  }
}

type PylonTheme = 'light' | 'dark';
type PylonWalletContext = { evmAddress?: string; solanaAddress?: string };

const PYLON_APP_ID = 'e586362e-bdd0-4f5d-a275-2879d9f2442a';
const PYLON_WIDGET_SRC = `https://widget.usepylon.com/widget/${PYLON_APP_ID}`;
const PYLON_THEME_MEDIA_QUERY = '(prefers-color-scheme: dark)';

// Pylon issue custom field slugs (must match the slugs configured in Pylon).
const PYLON_FIELD_EVM_WALLET = 'connected_evm_wallet';
const PYLON_FIELD_SOLANA_WALLET = 'connected_solana_wallet';
const PYLON_FIELD_SESSION_REPLAY = 'amplitude_session_replay';

// Latest connected wallet addresses, cached so showPylonChat (a bare click
// handler with no React context) can re-push them when the user opens the chat.
let connectedWalletContext: PylonWalletContext = {};

let initializeSessionPromise: Promise<boolean> | undefined;

// Fallback for sessions with no interaction: load the bundle anyway after this long.
const PYLON_WIDGET_LOAD_FALLBACK_MS = 5_000;
// First user interaction finalises LCP, so loading the heavy bundle then keeps it
// off the LCP path. tap/keypress/scroll match the events that finalise LCP.
const PYLON_WIDGET_LOAD_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const;
// #pylon-chat-bubble is Pylon's only readiness signal (no onReady/onLoad callback).
const PYLON_SESSION_READY_TIMEOUT_MS = 10_000;
const PYLON_BUBBLE_POLL_INTERVAL_MS = 500;

function cleanupPylonThemeSync() {
  window.__matchaPylonThemeSyncCleanup?.();
  window.__matchaPylonThemeSyncCleanup = undefined;
}

function getPylonTheme(): PylonTheme {
  const theme = document.documentElement.dataset['theme'] ?? document.body.dataset['theme'];

  if (theme === 'dark') {
    return 'dark';
  }

  if (theme === 'light') {
    return 'light';
  }

  return window.matchMedia(PYLON_THEME_MEDIA_QUERY).matches ? 'dark' : 'light';
}

function syncPylonTheme() {
  // The theme observer is a long-lived global callback; guard against Pylon not
  // being initialised so a stray mutation can never throw.
  if (typeof window.Pylon !== 'function') {
    return;
  }

  window.Pylon('setTheme', getPylonTheme());
}

function observePylonThemeChanges() {
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.attributeName === 'data-theme')) {
      syncPylonTheme();
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  const mediaQuery = window.matchMedia(PYLON_THEME_MEDIA_QUERY);
  const usesEventListener = typeof mediaQuery.addEventListener === 'function';

  if (usesEventListener) {
    mediaQuery.addEventListener('change', syncPylonTheme);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(syncPylonTheme);
  }

  return () => {
    observer.disconnect();

    if (usesEventListener) {
      mediaQuery.removeEventListener('change', syncPylonTheme);
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(syncPylonTheme);
    }
  };
}

// Pylon reads chat_settings when the bundle executes, so it must be set first —
// otherwise the session boots with no context (the empty custom-field bug).
function setPylonChatSettings() {
  window.pylon = {
    chat_settings: { app_id: PYLON_APP_ID, email: 'user@matcha.xyz', name: 'User' },
  };
}

/** Injects the widget bundle off the LCP path; boots the session on load. */
function loadPylonWidget() {
  if (document.querySelector(`script[src="${PYLON_WIDGET_SRC}"]`)) {
    return;
  }

  setPylonChatSettings();

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = PYLON_WIDGET_SRC;
  script.addEventListener('load', () => {
    void ensurePylonSession().catch(() => undefined);
  });

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

// Loads the widget on the first user interaction (which finalises LCP, so the heavy
// bundle never competes with it), or after a fallback timeout for sessions with no
// interaction. Idempotent: the loaded guard makes a late fallback fire a no-op, and
// loadPylonWidget itself bails if the script already exists.
function schedulePylonWidgetLoad() {
  let loaded = false;

  const load = () => {
    if (loaded) return;
    loaded = true;

    PYLON_WIDGET_LOAD_EVENTS.forEach((event) => window.removeEventListener(event, load, true));
    loadPylonWidget();
  };

  PYLON_WIDGET_LOAD_EVENTS.forEach((event) =>
    window.addEventListener(event, load, { once: true, passive: true, capture: true }),
  );

  setTimeout(load, PYLON_WIDGET_LOAD_FALLBACK_MS);
}

export function initializePylon() {
  // Synchronous queue shim so early Pylon(…) calls are replayed once the bundle loads.
  // Only install it if Pylon isn't already present, so a repeated init (e.g. Fast
  // Refresh) can't clobber a loaded bundle's real API or an existing shim's queue.
  if (typeof window.Pylon !== 'function') {
    const queue = function (...args: unknown[]) {
      queue.e(args);
    } as Window['Pylon'];
    queue.q = [] as unknown[];
    queue.e = function (event: unknown) {
      queue.q.push(event);
    };
    window.Pylon = queue;
  }

  syncPylonTheme();
  if (window.matchMedia(breakpoints.mobile).matches) {
    window.Pylon('hideChatBubble');
  }
  cleanupPylonThemeSync();

  const cleanup = observePylonThemeChanges();
  window.__matchaPylonThemeSyncCleanup = cleanup;

  schedulePylonWidgetLoad();

  return () => {
    if (window.__matchaPylonThemeSyncCleanup === cleanup) {
      cleanupPylonThemeSync();
    }
  };
}

/**
 * Attaches debugging context (connected wallets and, when available, an Amplitude
 * session replay link) to new Pylon chat issues via `setNewIssueCustomFields`.
 *
 * Wallet fields are always sent — an empty string clears a stale address from a
 * previous connection in this session. The session replay field is only included
 * when the current session is being recorded; the id is read fresh on each push
 * because it may only become available partway through a session.
 */
function pushPylonSupportContext() {
  if (typeof window === 'undefined' || typeof window.Pylon !== 'function') {
    return;
  }

  const replayId = getSessionReplayId();

  window.Pylon('setNewIssueCustomFields', {
    [PYLON_FIELD_EVM_WALLET]: connectedWalletContext.evmAddress ?? '',
    [PYLON_FIELD_SOLANA_WALLET]: connectedWalletContext.solanaAddress ?? '',
    ...(replayId ? { [PYLON_FIELD_SESSION_REPLAY]: getSessionReplayUrl(replayId) } : {}),
  });
}

async function initializePylonSession() {
  return new Promise<boolean>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let initializationInterval: ReturnType<typeof setInterval> | undefined = undefined;

    // Ensure chat_settings is present in case the bundle loader hasn't run yet
    // (e.g. a support click that beats the idle-scheduled widget load).
    setPylonChatSettings();

    const clearTimers = () => {
      clearTimeout(timeout);
      clearInterval(initializationInterval);
    };

    timeout = setTimeout(() => {
      initializeSessionPromise = undefined;
      clearTimers();
      reject(new Error('Pylon widget timeout'));
    }, PYLON_SESSION_READY_TIMEOUT_MS);

    initializationInterval = setInterval(() => {
      const pylonChatElement = document.getElementById('pylon-chat-bubble');

      if (pylonChatElement) {
        // Push now the session is booted, so the form captures real values as
        // defaults rather than the empty-string fallbacks.
        pushPylonSupportContext();
        clearTimers();
        resolve(true);
      }
    }, PYLON_BUBBLE_POLL_INTERVAL_MS);
  });
}

// Make sure to call initializePylon before initializing a Pylon session
export async function ensurePylonSession() {
  if (initializeSessionPromise) {
    return initializeSessionPromise;
  }

  initializeSessionPromise = initializePylonSession();
  return initializeSessionPromise;
}

/** Test-only: clears the intentionally-memoised session promise between tests. */
export function __resetPylonSessionForTests() {
  initializeSessionPromise = undefined;
}

/**
 * Updates the cached connected wallet addresses. Call whenever the connected
 * wallets change; showPylonChat pushes the cached context to Pylon when needed.
 */
export function setPylonWalletContext(context: PylonWalletContext) {
  connectedWalletContext = context;
}

export async function showPylonChat() {
  try {
    await ensurePylonSession();
  } catch (error) {
    logger.warn('Initialize pylon session error', error);
    return;
  }

  syncPylonTheme();

  // Re-push so the issue carries the freshest session replay id and wallet state
  // at the moment the user can create an issue.
  pushPylonSupportContext();

  // Re-push when the chat (and its form) is actually presented — onShow is the
  // only Pylon-emitted lifecycle event available.
  window.Pylon('onShow', () => pushPylonSupportContext());
  window.Pylon(
    'onHide',
    window.matchMedia(breakpoints.mobile).matches ? () => window.Pylon('hideChatBubble') : null,
  );
  window.Pylon('show');
}
